import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Play, Download, Volume2 } from "lucide-react";
import { useAudio } from "../context/AudioContext";

const line = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 + i * 0.09 },
  }),
};

const PREVIEW_SRC = "/api/media/tracks/01_FATHERSDAY.mp3";
const PREVIEW_LOOP_SECONDS = 15;
const FADE_MS = 550;
const TARGET_VOLUME = 0.55;

function useHoverPreview(mainPlaying) {
  const audioRef = useRef(null);
  const fadeRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const a = new Audio(`${BACKEND_URL}${PREVIEW_SRC}`);
    a.preload = "none";
    a.volume = 0;
    audioRef.current = a;
    const onTime = () => {
      if (a.currentTime >= PREVIEW_LOOP_SECONDS) a.currentTime = 0;
    };
    a.addEventListener("timeupdate", onTime);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.pause();
      audioRef.current = null;
      if (fadeRef.current) clearInterval(fadeRef.current);
    };
  }, []);

  const fadeTo = useCallback((target, onDone) => {
    const a = audioRef.current;
    if (!a) return;
    if (fadeRef.current) clearInterval(fadeRef.current);
    const steps = 20;
    const stepMs = FADE_MS / steps;
    const start = a.volume;
    let i = 0;
    fadeRef.current = setInterval(() => {
      i += 1;
      a.volume = Math.max(0, Math.min(1, start + (target - start) * (i / steps)));
      if (i >= steps) {
        clearInterval(fadeRef.current);
        fadeRef.current = null;
        if (onDone) onDone();
      }
    }, stepMs);
  }, []);

  const enter = useCallback(() => {
    if (mainPlaying) return;                                    // don't hijack main player
    if (typeof window !== "undefined" && window.matchMedia && !window.matchMedia("(hover: hover)").matches) return;
    const a = audioRef.current;
    if (!a) return;
    setActive(true);
    a.play().then(() => fadeTo(TARGET_VOLUME)).catch(() => setActive(false));
  }, [mainPlaying, fadeTo]);

  const leave = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    setActive(false);
    fadeTo(0, () => {
      a.pause();
      a.currentTime = 0;
    });
  }, [fadeTo]);

  // If main player starts, immediately stop preview
  useEffect(() => {
    if (mainPlaying) leave();
  }, [mainPlaying, leave]);

  return { active, enter, leave };
}

export default function Hero({ cover, onDownload }) {
  const ref = useRef(null);
  const { playIndex, isPlaying } = useAudio();
  const { active: previewActive, enter, leave } = useHoverPreview(isPlaying);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const coverScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const coverY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full flex flex-col justify-between overflow-hidden border-b border-zinc-900"
    >
      {/* top meta bar */}
      <motion.div
        className="relative z-20 flex items-center justify-between px-5 md:px-10 pt-6 font-mono text-[10px] md:text-xs uppercase tracking-widest text-zinc-500"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      >
        <span>Debut Mixtape</span>
        <span className="hidden sm:block">Est. MMXXV</span>
        <span>10 Tracks</span>
      </motion.div>

      {/* center */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 py-10">
        <motion.div
          style={{ scale: coverScale, y: coverY }}
          className="relative mb-8 md:mb-10"
        >
          <motion.div
            className="group relative p-3 md:p-4 border border-white/10 cursor-pointer"
            onMouseEnter={enter}
            onMouseLeave={leave}
            onFocus={enter}
            onBlur={leave}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              data-testid="cover-art"
              src={cover}
              alt="FRE3BRD cover art"
              className="w-[240px] h-[240px] sm:w-[300px] sm:h-[300px] md:w-[360px] md:h-[360px] object-contain select-none"
              draggable={false}
            />
            {/* Preview snippet hint / indicator (desktop hover only) */}
            <div
              data-testid="preview-hint"
              className={`hidden md:flex pointer-events-none absolute top-3 right-3 items-center gap-2 px-2.5 py-1.5 bg-black/70 backdrop-blur-md border border-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                previewActive ? "!opacity-100" : ""
              }`}
            >
              <Volume2 size={11} className={`text-white ${previewActive ? "animate-pulse" : ""}`} />
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white">
                {previewActive ? "Sampling" : "Hover to sample"}
              </span>
            </div>
          </motion.div>
        </motion.div>

        <motion.h1
          style={{ y: titleY }}
          className="text-center font-display font-black leading-[0.82] tracking-tighter text-white text-6xl sm:text-8xl md:text-9xl"
        >
          <span className="block overflow-hidden">
            <motion.span
              className="inline-block"
              variants={line}
              custom={0}
              initial="hidden"
              animate="visible"
            >
              FRE3BRD
            </motion.span>
          </span>
        </motion.h1>

        <div className="mt-6 overflow-hidden">
          <motion.p
            className="font-mono text-[11px] md:text-sm uppercase tracking-[0.35em] text-zinc-400"
            variants={line}
            custom={1}
            initial="hidden"
            animate="visible"
          >
            Listen · Download Free · Stream
          </motion.p>
        </div>

        {/* CTAs */}
        <motion.div
          className="mt-9 flex flex-col sm:flex-row items-center gap-3"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
        >
          <button
            data-testid="hero-play-btn"
            onClick={() => playIndex(0)}
            className="group flex items-center gap-3 bg-white text-black px-7 py-3.5 font-mono uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors"
          >
            <Play size={15} className="fill-black" />
            Play Tape
          </button>
          <button
            data-testid="hero-download-btn"
            onClick={onDownload}
            className="group flex items-center gap-3 border border-zinc-700 text-white px-7 py-3.5 font-mono uppercase tracking-widest text-xs hover:border-white hover:bg-white hover:text-black transition-all"
          >
            <Download size={15} />
            Free Download
          </button>
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex items-center justify-center pb-8 font-mono text-[10px] uppercase tracking-widest text-zinc-500"
      >
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="flex items-center gap-2"
        >
          <ArrowDown size={13} /> Scroll to tracklist
        </motion.div>
      </motion.div>
    </section>
  );
}
